import { useState, useCallback, useMemo, ChangeEvent, Dispatch, SetStateAction, useEffect } from 'react';
import debounce from 'lodash/debounce';

type UseDebouncedInputValueReturn<T> = [T, (event: ChangeEvent<HTMLInputElement>) => void, () => void];

type SetterOrCallback<T> = Dispatch<SetStateAction<T>> | ((value: T) => void);

interface UseDebouncedInputValueProps<T> {
    state: T;
    setState: SetterOrCallback<T>;
    wait?: number;
}

// sync external state update with local value
const useDebouncedInputValue = <T extends string | number>({
    state,
    setState,
    wait = 300,
}: UseDebouncedInputValueProps<T>): UseDebouncedInputValueReturn<T> => {
    const [stateLocal, setStateLocal] = useState<T>(state);

    useEffect(() => {
        setStateLocal(state);
    }, [state]);

    const handleClearState = () => {
        setStateLocal('' as T);
        handleDebounceSetState.cancel(); // fast clearing without debounce
        setState('' as T);
    };

    const handleSetState = useCallback(
        (newValue: T) => {
            setState(newValue);
        },
        [setState],
    );

    const handleDebounceSetState = useMemo(() => debounce(handleSetState, wait), [handleSetState, wait]);

    const handleChangeState = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as T;
        setStateLocal(value);
        handleDebounceSetState(value);
    };

    return [stateLocal, handleChangeState, handleClearState];
};

export default useDebouncedInputValue;
